import { Injectable } from '@angular/core'
import { AppDownloadService } from '@ftr/api-core'
import { UserGroupPermissionId } from '@ftr/contracts/api/user-group'
import { FeatureName, ProductFeature } from '@ftr/contracts/type/core'
import { Uuid } from '@ftr/contracts/type/shared'
import { CourtSystemsState, FetchCourtSystemProductFeaturesAction } from '@ftr/data-court-system'
import { ErrorPageService, isNotNullOrUndefined, titleCase, unwrapData } from '@ftr/foundation'
import { AppPaths, CourtSystemPaths } from '@ftr/routing-paths'
import { FeatureFlagState } from '@ftr/ui-feature-flags'
import { FavoritesState, FetchFavoritesForCourtSystemAction, UserState } from '@ftr/ui-user'
import { VocabularyTermsService } from '@ftr/ui-vocab'
import { Store } from '@ngxs/store'
import { combineLatest, filter, map, Observable, of, switchMap, tap } from 'rxjs'
import { CourtSystemNavItems } from '../court-system-nav-items'
import { getGlobalAdminNavItem, getHelpSupportNavItem, SideNavGroup, SideNavItem } from '../side-nav-item'
import { IconUrlMapperService } from './icon-mapping-service'

const ADMIN_PAGE_PERMISSIONS = [
  UserGroupPermissionId.ManageRealtimeRecorders,
  UserGroupPermissionId.ConfigureCourtSystem,
  UserGroupPermissionId.ConfigureDepartments,
  UserGroupPermissionId.AdministerLocations,
  UserGroupPermissionId.AdministerHolidays,
  UserGroupPermissionId.RemoveTrms,
  UserGroupPermissionId.AdministerBillingGroups,
  UserGroupPermissionId.AccessAuditLogs,
]

@Injectable({
  providedIn: 'root',
})
export class InternalUserNavItemsProvider {
  constructor(
    private readonly store: Store,
    private readonly courtSystemNavItems: CourtSystemNavItems,
    private readonly appDownloadService: AppDownloadService,
    private readonly iconMapperService: IconUrlMapperService,
    private readonly errorPageService: ErrorPageService,
    private readonly vocabularyTermsService: VocabularyTermsService,
  ) {}

  provide(currentCourtSystemId: Uuid): Observable<{ navGroups: SideNavGroup[]; footerItems: SideNavItem[] }> {
    this.store.dispatch(new FetchCourtSystemProductFeaturesAction(currentCourtSystemId))
    const userPermissionsAndGlobalAdmin = combineLatest([
      this.store.select(UserState.getEnabledPermissions).pipe(map(fn => fn(currentCourtSystemId).map(p => p.id))),
      this.store.select(UserState.isGlobalAdmin),
    ])

    return combineLatest([
      userPermissionsAndGlobalAdmin.pipe(
        tap(([userPermissionIds, isGlobalAdmin]) => {
          if (!isGlobalAdmin && userPermissionIds.length === 0) {
            this.errorPageService.showNotFoundPage()
          }
        }),
      ),
      this.store.select(UserState.currentCourtSystemEnabledFeatures).pipe(unwrapData()),
      this.vocabularyTermsService.observeTerms(currentCourtSystemId).pipe(filter(isNotNullOrUndefined)),
      this.store.select(UserState.user).pipe(
        unwrapData(),
        switchMap(user => {
          this.store.dispatch(new FetchFavoritesForCourtSystemAction(currentCourtSystemId))
          return of(user)
        }),
        switchMap(() => {
          return combineLatest([
            this.store.select(FavoritesState.favorites(currentCourtSystemId)),
            this.store.select(FavoritesState.isLoading),
          ])
        }),
        map(([favorites, favoritesLoading]) => ({ favoritesLoading, favorites })),
      ),
      this.store.select(UserState.currentCourtSystemConfiguration).pipe(unwrapData()),
      this.store.select(CourtSystemsState.courtSystemProductFeatures(currentCourtSystemId)),
      this.store.select(FeatureFlagState.featureFlag('public-navigation')),
    ]).pipe(
      map(
        ([
          [userPermissionIds, isGlobalAdmin],
          currentCourtSystemEnabledFeatures,
          vocabularyTerms,
          favoritesConfiguration,
          courtSystemConfiguration,
          currentCourtSystemProductFeatures,
          publicNavigationFeatureEnabled,
          // eslint-disable-next-line complexity
        ]) => {
          const navGroups: SideNavGroup[] = []
          const footerItems: SideNavItem[] = []

          const resourceGroup: SideNavGroup = {
            items: [],
          }

          resourceGroup.items?.push({
            type: 'LINK',
            icon: 'Courthouse',
            title: titleCase(vocabularyTerms.courthouse.plural),
            path: [`/${AppPaths.CourtSystem}`, currentCourtSystemId, CourtSystemPaths.Locations],
          } as SideNavItem)

          if (courtSystemConfiguration.isDepartmentsEnabled) {
            resourceGroup.items?.push({
              type: 'LINK',
              icon: 'Department',
              ...this.courtSystemNavItems.departments(currentCourtSystemId, vocabularyTerms),
            } as SideNavItem)
          }

          if (userPermissionIds.includes(UserGroupPermissionId.PlaybackHearings)) {
            resourceGroup.items?.push({
              type: 'LINK',
              icon: 'Case',
              ...this.courtSystemNavItems.cases(currentCourtSystemId, vocabularyTerms),
            } as SideNavItem)
          }

          if (currentCourtSystemProductFeatures.includes(ProductFeature.JudgeAnnotationsLibrary)) {
            resourceGroup.items?.push({
              type: 'LINK',
              icon: 'Judge',
              ...this.courtSystemNavItems.judges(currentCourtSystemId, vocabularyTerms),
            } as SideNavItem)
          }

          if (resourceGroup.items?.length) {
            navGroups.push(resourceGroup)
          }

          navGroups.push({
            title: 'Favorites',
            isLoading: favoritesConfiguration.favoritesLoading,
            items: favoritesConfiguration.favorites?.map(favorite => {
              const [path, queryParams] = urlToRouteLinkArray(favorite.url)
              return {
                type: 'LINK',
                icon: this.iconMapperService.getIconForUrl(favorite.url),
                title: favorite.label,
                path,
                queryParams,
              } as SideNavItem
            }),
          })

          if (userPermissionIds.includes(UserGroupPermissionId.PlaybackRecordings)) {
            navGroups.push({
              title: 'Content',
              items: [
                {
                  type: 'LINK',
                  icon: 'Recordings',
                  ...this.courtSystemNavItems.recordings(currentCourtSystemId),
                } as SideNavItem,
              ],
            })
          }

          const orderingLabel = publicNavigationFeatureEnabled ? 'Request Processing' : 'Order Processing'

          const orderProcessingGroup: SideNavGroup = {
            title: orderingLabel,
            items: [],
          }

          if (userPermissionIds.includes(UserGroupPermissionId.ProcessAudioOrders)) {
            orderProcessingGroup.items?.push({
              type: 'LINK',
              icon: 'AudioOrder',
              ...this.courtSystemNavItems.audioOrders(currentCourtSystemId),
              title: 'Audio',
              tooltipHint: `Audio ${orderingLabel}`,
            } as SideNavItem)
          }

          if (userPermissionIds.includes(UserGroupPermissionId.ProcessRealTimeOrders)) {
            const title = 'Live'
            orderProcessingGroup.items?.push({
              type: 'LINK',
              icon: 'RealtimeOrder',
              ...this.courtSystemNavItems.realTimeOrders(currentCourtSystemId),
              title,
              tooltipHint: `${title} ${orderingLabel}`,
            } as SideNavItem)
          }

          if (userPermissionIds.includes(UserGroupPermissionId.ProcessTranscriptOrders)) {
            orderProcessingGroup.items?.push({
              type: 'LINK',
              icon: 'TranscriptOrder',
              ...this.courtSystemNavItems.transcriptOrders(currentCourtSystemId),
              title: 'Transcript',
              tooltipHint: `Transcript ${orderingLabel}`,
            } as SideNavItem)
          }

          if (orderProcessingGroup.items?.length) {
            navGroups.push(orderProcessingGroup)
          }

          const managementGroup: SideNavGroup = {
            title: 'Management',
            items: [],
          }

          const isRealTimeRecordersEnabled = !!currentCourtSystemEnabledFeatures?.some(
            feature => feature.name === FeatureName.RealTimeRecorders,
          )

          if (
            (isGlobalAdmin && isRealTimeRecordersEnabled) ||
            (userPermissionIds.includes(UserGroupPermissionId.ConfigureCourtSystem) &&
              currentCourtSystemProductFeatures.includes(ProductFeature.CourtMonitoring))
          ) {
            managementGroup.items?.push({
              type: 'LINK',
              icon: 'CourtMonitoring',
              ...this.courtSystemNavItems.courtMonitoring(currentCourtSystemId),
              title: 'Monitoring',
            } as SideNavItem)
          }

          if (userPermissionIds.includes(UserGroupPermissionId.ViewRealTimeRecorders) && isRealTimeRecordersEnabled) {
            managementGroup.items?.push({
              type: 'LINK',
              icon: 'Recorder',
              ...this.courtSystemNavItems.producers(currentCourtSystemId),
              title: 'Recorders',
            } as SideNavItem)
          }

          if (userPermissionIds.includes(UserGroupPermissionId.AdministerUserGroups)) {
            managementGroup.items?.push({
              type: 'LINK',
              icon: 'People',
              ...this.courtSystemNavItems.courtUsers(currentCourtSystemId),
              title: 'Users & Groups',
            } as SideNavItem)
          }

          if (userPermissionIds.includes(UserGroupPermissionId.WriteRecordingVault)) {
            managementGroup.items?.push({
              type: 'LINK',
              icon: 'TrmRepositories',
              ...this.courtSystemNavItems.recordingVault(currentCourtSystemId),
              title: 'TRM Repositories',
            } as SideNavItem)
          }

          if (userPermissionIds.includes(UserGroupPermissionId.AccessReports)) {
            managementGroup.items?.push({
              type: 'LINK',
              icon: 'BarGraphVertical',
              ...this.courtSystemNavItems.reports(currentCourtSystemId),
              title: 'Reports',
            } as SideNavItem)
          }

          if (userPermissionIds.some(id => ADMIN_PAGE_PERMISSIONS.includes(id))) {
            managementGroup.items?.push({
              type: 'LINK',
              icon: 'Settings',
              ...this.courtSystemNavItems.adminMenu(currentCourtSystemId),
              title: 'Settings',
            } as SideNavItem)
          }

          if (managementGroup.items?.length) {
            navGroups.push(managementGroup)
          }

          if (isGlobalAdmin) {
            footerItems.push(getGlobalAdminNavItem(currentCourtSystemId))
          }

          footerItems.push(getHelpSupportNavItem(this.appDownloadService.getStandaloneAppLink()))

          return { navGroups, footerItems }
        },
      ),
    )
  }
}

const urlToRouteLinkArray = (url: string): any[] => {
  const [path, searchString] = url.split('?')
  if (!searchString) {
    return [path]
  }
  const searchParams = new URLSearchParams(searchString)
  const queryObject: Record<string, string> = {}
  for (const [key, value] of searchParams) {
    queryObject[key] = value
  }
  return [path, queryObject]
}
